import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import moment from "moment";
import {
  MISSION_GOAL,
  missionTypeFilterOptions,
  competitionFormatFilterOptions,
  missionGoalOptions,
} from "utils/constants/options";
import { getTargetUnitLabel } from "utils/functions/companyMissionRender";
import { BoxConfirmAction, Buttons, Displays, FormFields, Icons, Label, Modal } from "components";
import { AddCompanyMissionV2FormStyled } from "./styled";
import createMissionFormSchema from "./schema";
import theme from "styles/theme.json";
import HeaderWithActions from "components/Displays/HeaderWithActions";
import RewardSharedGoal from "./RewardScore/RewardSharedGoal";
import RewardHighestScore from "./RewardScore/RewardHighestScore";

const AddCompanyMissionV2Form = ({
  initialValues,
  onSubmit,
  isCreate,
  onBack,
  onDeleteClick,
  progressData,
  onTeamManagementClick,
  onLeaderboardClick,
  companyOptions,
}) => {
  const isInitialMount = useRef(true);
  const minTeamCount = isCreate ? 0 : Number(initialValues?.team_count) || 0;
  const missionFormSchema = useMemo(() => createMissionFormSchema(minTeamCount), [minTeamCount]);
  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    clearErrors,
    formState: { isValid, isDirty, errors: formErrors },
  } = useForm({
    resolver: yupResolver(missionFormSchema),
    mode: "onChange",
    defaultValues: {
      name_th: "",
      name_en: "",
      category: null,
      challenge_type: null,
      company_ids: [],
      begin_date: null,
      end_date: null,
      mission_goal: null,
      target_amount: "",
      success_coin_amount: "",
      fail_coin_amount: "",
      reward_1st_amount: "",
      reward_2nd_amount: "",
      reward_3rd_amount: "",
      reward_consolation_amount: "",
      image_file_th: [],
      image_file_en: [],
      registration_start_date: null,
      register_end_date: null,
      team_count: "",
      team_size: "",
    },
  });

  const watchedCategory = watch("category");
  const watchedChallengeType = watch("challenge_type");
  const watchedMissionGoal = watch("mission_goal");
  const watchedBegin = watch("begin_date");
  const watchedRegistrationDate = watch("registration_start_date");
  const watchedEnd = watch("end_date");
  const categoryValue = watchedCategory?.value;
  const challengeTypeValue = watchedChallengeType?.value;
  const missionGoalValue = watchedMissionGoal?.value;
  const targetUnitLabel = getTargetUnitLabel(categoryValue);
  const isTeamFormat = challengeTypeValue === "team";
  const filteredMissionGoalOptions = useMemo(() => {
    if (isTeamFormat) {
      return missionGoalOptions.filter((o) => o.value !== MISSION_GOAL.SHARED_GOAL.value);
    }
    return missionGoalOptions;
  }, [isTeamFormat]);
  const isSharedOrFirstGoal =
    missionGoalValue === MISSION_GOAL.SHARED_GOAL.value ||
    missionGoalValue === MISSION_GOAL.FIRST_COMPLETE.value;
  const isHighestScoreGoal = missionGoalValue === MISSION_GOAL.HIGHEST_SCORE.value;
  const isStatusPending = progressData?.status === 'pending';
  const isStatusRegistration = progressData?.status === 'registration';
  const isStatusInProgress = progressData?.status === 'active';
  const isStatusExpired = progressData?.status === 'expired';
  const isEditLocked = isStatusRegistration || isStatusInProgress || isStatusExpired;
  const isFullyLocked = isStatusPending || isEditLocked;
  const isProgressOrExpired = isStatusInProgress || isStatusExpired;

  const [isShowBackConfirm, setIsShowBackConfirm] = useState(false);

  const handleBackClick = useCallback(() => {
    if (isDirty) {
      setIsShowBackConfirm(true);
    } else {
      onBack();
    }
  }, [isDirty, onBack]);

  useEffect(() => {
    if (initialValues) {
      reset(initialValues);
    }
  }, [initialValues, reset]);

  useEffect(() => {
    if (watchedChallengeType?.value === "department") {
      setValue("registration_start_date", null, { shouldValidate: true, shouldDirty: false });
      setValue("register_end_date", null, { shouldValidate: true, shouldDirty: false });
      setValue("team_count", "", { shouldValidate: true, shouldDirty: false });
      setValue("team_size", "", { shouldValidate: true, shouldDirty: false });
    } else if (watchedChallengeType?.value === "team") {
      const endVal = getValues("end_date");
      if (endVal) {
        setValue("register_end_date", endVal, { shouldValidate: true, shouldDirty: false });
      }
      const currentGoal = getValues("mission_goal");
      if (currentGoal?.value === MISSION_GOAL.SHARED_GOAL.value) {
        setValue("mission_goal", null, { shouldValidate: true, shouldDirty: false });
      }
    }
  }, [watchedChallengeType?.value, setValue, getValues]);


  useEffect(() => {
    if (isInitialMount.current) return;
    if (categoryValue) {
      setValue("target_amount", "", { shouldValidate: false, shouldDirty: false });
    }
  }, [categoryValue, setValue]);

  const handleMissionGoalChange = useCallback((value) => {
    setValue("target_amount", "");
    if (
      value?.value === MISSION_GOAL.SHARED_GOAL.value ||
      value?.value === MISSION_GOAL.FIRST_COMPLETE.value
    ) {
      setValue("success_coin_amount", "");
      setValue("fail_coin_amount", "");
    } else if (value?.value === MISSION_GOAL.HIGHEST_SCORE.value) {
      setValue("reward_1st_amount", "");
      setValue("reward_2nd_amount", "");
      setValue("reward_3rd_amount", "");
      setValue("reward_consolation_amount", "");
    }
  }, [setValue]);

  const handleBeginDateChange = useCallback(
    (date) => {
      const endVal = getValues("end_date");
      if (date && endVal && moment(endVal).isSameOrBefore(moment(date))) {
        const next = new Date(date);
        next.setDate(next.getDate() + 1);
        setValue("end_date", next, { shouldValidate: true });
      }
      clearErrors("end_date");
      const regStart = getValues("registration_start_date");
      if (date && regStart && moment(regStart).isSameOrAfter(moment(date))) {
        setValue("registration_start_date", null, { shouldValidate: true });
      }
    },
    [clearErrors, getValues, setValue]
  );

  const handleEndDateChange = useCallback(
    (date) => {
      if (isTeamFormat && date) {
        setValue("register_end_date", date, { shouldValidate: true });
      }
    },
    [isTeamFormat, setValue]
  );

  const beginDateMinDate = (() => {
    const base = moment().add(2, 'days');
    if (isTeamFormat && watchedRegistrationDate) {
      const regStartPlus1 = moment(watchedRegistrationDate).add(1, 'days');
      return (regStartPlus1.isAfter(base) ? regStartPlus1 : base).toDate();
    }
    return base.toDate();
  })();

  const endDateMinDate = (() => {
    const base = watchedBegin
      ? moment(watchedBegin).add(1, "days")
      : moment().add(3, "days");
    if (!isCreate && initialValues?.end_date && !isStatusPending) {
      const original = moment(initialValues.end_date);
      return (original.isAfter(base) ? original : base).toDate();
    }
    return base.toDate();
  })();

  return (
    <AddCompanyMissionV2FormStyled>
      <form>
        <div className="header_block">
          <HeaderWithActions
            theme_admin
            handleBackCustom={handleBackClick}
            title={isCreate ? "สร้างภารกิจ" : isStatusExpired ? "รายละเอียดภารกิจ" : "แก้ไขภารกิจ"}
            subTitle={isCreate ? "Create Mission" : isStatusExpired ? "Mission Detail" : "Edit Mission"}
            typeObj={
              <div className="header_actions">
                {!isCreate && !isStatusExpired && (
                  <div className="bottom_delete_only">
                    <Buttons.BtnNormal
                      theme_only_border_red
                      type="button"
                      label="ลบภารกิจ"
                      paddingButton="10px 35px"
                      borderRadius="8px"
                      onClick={onDeleteClick}
                      svg_front={<Icons.Delete5 />}
                    />
                  </div>
                )}
                {!isStatusExpired &&
                  <div className="header_save_wrap">
                    <Buttons.BtnNormal
                      onClick={() => handleSubmit(onSubmit)()}
                      theme_only_border_blue
                      label="บันทึก"
                      paddingButton="10px 35px"
                      borderRadius="8px"
                      svg_front={<Icons.Disk color={!(isValid && isDirty) ? '#949494' : theme.COLORS.PURPLE_1} />}
                      disabled={!(isValid && isDirty)}
                    />
                  </div>
                }
              </div>}
          />
        </div>
        {!isCreate && progressData && (
          <Displays.MissionProgressSection
            progressData={progressData}
            isLeaderboardDisabled={isStatusPending || isStatusRegistration}
            onLeaderboardClick={onLeaderboardClick}
          />
        )}
        <div className="section_block">
          <div className="section_header">
            <Label.LabelInlineIcon
              theme_title
              icon={<Icons.Rocket />}
              label={'รายละเอียดภารกิจ'}
            />
          </div>

          <div className="stack_gap">
            <div className="row_2col">
              <FormFields.FormUploadNew
                theme_upload_set_image
                control={control}
                name="image_file_th"
                label="ภาพภารกิจ (TH)"
                subLabel="Mission Image (TH)"
                required
                acceptType="cover"
                maxMBFileSizeWarning="5"
                maxLength={1}
                multiple={false}
                width="100%"
                heightBox="289px"
                labelTop="อัปโหลดไฟล์รูปภาพ"
                desc_text="รองรับไฟล์ : .jpg/jpeg/png  Resolution : 1,200 x 675  ขนาดไฟล์สูงสุดไม่เกิน : 5 MB"
                widthImageSize={1200}
                disabled={isStatusExpired}
                heighImageSize={675}
              />
              <FormFields.FormUploadNew
                theme_upload_set_image
                control={control}
                name="image_file_en"
                label="ภาพภารกิจ (EN)"
                subLabel="Mission Image (EN)"
                required
                acceptType="cover"
                maxMBFileSizeWarning="5"
                maxLength={1}
                multiple={false}
                width="100%"
                heightBox="289px"
                labelTop="อัปโหลดไฟล์รูปภาพ"
                desc_text="รองรับไฟล์ : .jpg/jpeg/png  Resolution : 1,200 x 675  ขนาดไฟล์สูงสุดไม่เกิน : 5 MB"
                widthImageSize={1200}
                disabled={isStatusExpired}
                heighImageSize={675}
              />
            </div>

            <div className="row_2col">
              <FormFields.FormTextField
                theme_standard_new
                control={control}
                name="name_th"
                label="ชื่อภารกิจ (TH)"
                subLabel="Mission Title (TH)"
                paddingInput="9px"
                placeholder="ชื่อภารกิจ"
                disabled={isEditLocked}
                maxLength={255}
                showCharCount
                required
              />
              <FormFields.FormTextField
                theme_standard_new
                control={control}
                name="name_en"
                label="ชื่อภารกิจ (EN)"
                subLabel="Mission Title (EN)"
                paddingInput="9px"
                placeholder="ชื่อภารกิจ"
                disabled={isEditLocked}
                maxLength={255}
                showCharCount
                required
              />
            </div>

            <div className="row_2col">
              <FormFields.FormAutoSuggestDropdownCheckbox
                control={control}
                name="company_ids"
                label="บริษัทที่เข้าร่วม"
                subLabel="Participating Companies"
                options={companyOptions || []}
                placeholder="เลือกบริษัทที่เข้าร่วม"
                showAllOption={false}
                showSearch={false}
                disabled={isFullyLocked}
                required
              />

              <div>
                <Label.LabelForm
                  label="ระยะเวลา"
                  subLabel="Period"
                  required
                />
                <div className="date_range_row">
                  <div className="date_range_field">
                    <FormFields.FormDatePickerNew
                      theme_standard
                      control={control}
                      name="begin_date"
                      placeholder="วันเริ่มภารกิจ"
                      disabled={isFullyLocked}
                      minDate={beginDateMinDate}
                      maxDate={watchedEnd ? moment(watchedEnd).subtract(1, 'days').toDate() : undefined}
                      onChangeCustom={handleBeginDateChange}
                    />
                  </div>
                  <div className="between_label">ถึง</div>
                  <div className="date_range_field">
                    <FormFields.FormDatePickerNew
                      theme_standard
                      control={control}
                      name="end_date"
                      placeholder="วันสิ้นสุดภารกิจ"
                      disabled={isStatusExpired}
                      minDate={endDateMinDate}
                      onChangeCustom={handleEndDateChange}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="section_block">
          <div className="section_header">
            <Label.LabelInlineIcon
              theme_title
              icon={<Icons.PeopleRunning />}
              label={'ประเภทและรูปแบบการแข่งขัน'}
            />
          </div>

          <div className="stack_gap">
            <div className="row_2col">
              <div className="dropdown_with_icon">
                <FormFields.FormAutoSuggestDropdown
                  control={control}
                  name="category"
                  label="ประเภทภารกิจ"
                  subLabel="Mission Type"
                  options={missionTypeFilterOptions}
                  placeholder="เลือกประเภทภารกิจ"
                  disabled={isFullyLocked}
                  showAllOption={false}
                  showSearch={false}
                  required
                />
              </div>
              <div className="dropdown_with_icon">
                <FormFields.FormAutoSuggestDropdown
                  control={control}
                  name="challenge_type"
                  label="รูปแบบการแข่งขัน"
                  disabled={!getValues("category") || isFullyLocked}
                  subLabel="Challenge Type"
                  options={competitionFormatFilterOptions}
                  placeholder="เลือกรูปแบบการแข่งขัน"
                  showAllOption={false}
                  showSearch={false}
                  required
                />
              </div>
            </div>

            {isTeamFormat && !isStatusInProgress ? (
              <>
                <div className="row_2col">
                  <div>
                    <Label.LabelForm
                      label="ระยะเวลาสำหรับการลงทะเบียน"
                      subLabel="Register Period"
                      required
                    />
                    <div className="date_range_row">
                      <div className="date_range_field">
                        <FormFields.FormDatePickerNew
                          theme_standard
                          control={control}
                          name="registration_start_date"
                          placeholder="วันเริ่มการลงทะเบียน"
                          minDate={moment().add(1, 'days').toDate()}
                          maxDate={watchedBegin ? moment(watchedBegin).subtract(1, 'days').toDate() : undefined}
                          disabled={isFullyLocked}
                        />
                      </div>
                      <div className="between_label">ถึง</div>
                      <div className="date_range_field">
                        <FormFields.FormDatePickerNew
                          control={control}
                          name="register_end_date"
                          theme_standard
                          placeholder="วันสิ้นสุดการลงทะเบียน"
                          disabled
                        />
                      </div>
                    </div>
                  </div>
                  <div className="row_2col_inner">
                    <FormFields.FormTextField
                      theme_standard_new
                      control={control}
                      name="team_count"
                      label="จำนวนทีม"
                      subLabel="Number of Teams"
                      paddingInput="9px"
                      useComma
                      disabled={isProgressOrExpired}
                      type="number"
                      placeholder="จำนวนทีม"
                      isNotStartNumberZero
                      maxLength={3}
                      required
                      errors={formErrors?.team_count?.message}
                    />
                    <FormFields.FormTextField
                      theme_standard_new
                      control={control}
                      name="team_size"
                      label="จำนวนผู้เข้าร่วม"
                      subLabel="Number of Participants"
                      paddingInput="9px"
                      isNotStartNumberZero
                      maxLength={3}
                      disabled={isFullyLocked}
                      useComma
                      type="number"
                      placeholder="จำนวนผู้เข้าร่วม"
                      required
                    />
                  </div>
                </div>
                {!isCreate && !isStatusPending && !isStatusExpired ? (
                  <div className="row_2col">
                    <div></div>
                    <div className="team_registration_row">
                      <p className="team_registration_summary">
                        ลงทะเบียนแล้วทั้งหมด
                        <span className="team_registration_num">{initialValues?.registered_teams ?? 0}</span>
                        ทีม รวม
                        <span className="team_registration_num">{initialValues?.registered_members ?? 0}</span>
                        คน
                      </p>
                      <div className="team_manage_btn_wrap">
                        <Buttons.BtnNormal
                          theme_only_border_blue
                          label={<span style={{ fontSize: "14px", textDecoration: "underline" }}>จัดการทีม</span>}
                          svg_front={<Icons.RewardEdit />}
                          borderRadius="8px"
                          fontSize="14px"
                          backgroundColor="#fff"
                          paddingButton="6px 16px"
                          onClick={() => onTeamManagementClick && onTeamManagementClick()}
                        />
                      </div>
                    </div>
                  </div>
                ) : null}

              </>
            ) : null}
          </div>
        </div>

        <div className="section_block">
          <div className="section_header">
            <Label.LabelInlineIcon
              theme_title
              icon={<Icons.RewardStar />}
              label={'เป้าหมายและรางวัล'}
            />
          </div>

          <div className="stack_gap">
            <div className="goal_target_row">
              <FormFields.FormAutoSuggestDropdown
                control={control}
                name="mission_goal"
                label="เป้าหมายภารกิจ"
                subLabel="Mission Goal"
                options={filteredMissionGoalOptions}
                placeholder="เลือกเป้าหมายภารกิจ"
                onChangeCustom={handleMissionGoalChange}
                disabled={!getValues("category") || isFullyLocked}
                showAllOption={false}
                showSearch={false}
                required
              />
              <div className="target_amount_cell">
                {getValues("mission_goal")?.value && getValues("mission_goal")?.value !== MISSION_GOAL.HIGHEST_SCORE.value && (
                  <FormFields.FormTextField
                    theme_standard_new
                    control={control}
                    name="target_amount"
                    label="จำนวนเป้าหมาย"
                    subLabel="Target amount"
                    paddingInput="9px"
                    useComma
                    isNotStartNumberZero
                    type="number"
                    maxLength={8}
                    disabled={isEditLocked}
                    iconRight={targetUnitLabel ? targetUnitLabel : null}
                    placeholder="จำนวนเป้าหมาย"
                    required
                  />
                )}
              </div>
            </div>

            {isSharedOrFirstGoal && (
              <RewardSharedGoal control={control} disabled={isEditLocked} />
            )}

            {isHighestScoreGoal && (
              <RewardHighestScore control={control} disabled={isEditLocked} />
            )}
          </div>
        </div>
      </form>

      <Modal
        theme_modal_standard
        isShow={isShowBackConfirm}
        handleClickCloseModal={() => setIsShowBackConfirm(false)}
      >
        <BoxConfirmAction
          textShow="ยืนยันการออกจากหน้านี้"
          textBottom={"คุณยังไม่ได้บันทึกข้อมูล\nหากกดออกจากหน้านี้ อาจทำให้ข้อมูลสูญหาย"}
          handleCancel={() => setIsShowBackConfirm(false)}
          handleConfirm={() => {
            setIsShowBackConfirm(false);
            onBack();
          }}
        />
      </Modal>
    </AddCompanyMissionV2FormStyled>
  );
};

export default AddCompanyMissionV2Form;
